import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiChevronLeft,
    FiChevronRight,
    FiRefreshCw,
    FiSearch,
    FiTruck,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchSupplierPurchaseSummary,
    supplierPurchaseSummaryRecordsSelector,
    supplierPurchaseSummaryTotalsSelector,
    supplierPurchaseSummaryPaginationSelector,
    purchaseReportLoadingSelector,
    purchaseReportErrorSelector,
} from "../../../../../../../store/billing/purchaseReport/purchaseReportSlice";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        toNumber(value)
    );


const getSupplierName = (
    record
) =>
    record?.supplier?.supplierName ||
    record?.supplier?.companyName ||
    record?.supplier?.name ||
    "Unknown Supplier";


const getSupplierCode = (
    record
) =>
    record?.supplier?.supplierCode ||
    "-";


const getSupplierContact = (
    record
) =>
    record?.supplier?.mobile ||
    record?.supplier?.phone ||
    "-";


const getSupplierGstin = (
    record
) =>
    record?.supplier?.gstin ||
    record?.supplier?.gstNumber ||
    "-";


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SupplierSummaryReport = ({
    fromDate,
    toDate,
    status,
    paymentStatus,
    taxType,
    financialYear,
}) => {
    const dispatch =
        useDispatch();


    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const records =
        useSelector(
            supplierPurchaseSummaryRecordsSelector
        ) || [];

    const summary =
        useSelector(
            supplierPurchaseSummaryTotalsSelector
        ) || {};

    const pagination =
        useSelector(
            supplierPurchaseSummaryPaginationSelector
        ) || {};

    const loading =
        useSelector(
            purchaseReportLoadingSelector
        ) || {};

    const errors =
        useSelector(
            purchaseReportErrorSelector
        ) || {};


    /*
    |--------------------------------------------------------------------------
    | LOCAL FILTERS
    |--------------------------------------------------------------------------
    */

    const [
        searchInput,
        setSearchInput,
    ] = useState("");

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);

    const [
        sortBy,
        setSortBy,
    ] = useState(
        "grandTotal"
    );

    const [
        sortOrder,
        setSortOrder,
    ] = useState(
        "desc"
    );


    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadData =
        useCallback(
            () => {
                dispatch(
                    fetchSupplierPurchaseSummary({
                        page,
                        limit,

                        fromDate,
                        toDate,

                        status,
                        paymentStatus,
                        taxType,
                        financialYear,

                        search,

                        sortBy,
                        sortOrder,
                    })
                );
            },
            [
                dispatch,

                page,
                limit,

                fromDate,
                toDate,

                status,
                paymentStatus,
                taxType,
                financialYear,

                search,

                sortBy,
                sortOrder,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | RESET PAGE WHEN PARENT FILTER CHANGES
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            setPage(1);
        },
        [
            fromDate,
            toDate,
            status,
            paymentStatus,
            taxType,
            financialYear,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | SEARCH DEBOUNCE
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        setSearch(
                            searchInput.trim()
                        );

                        setPage(1);
                    },
                    350
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            searchInput,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | SUMMARY CARDS
    |--------------------------------------------------------------------------
    */

    const cards =
        useMemo(
            () => [
                {
                    label:
                        "Suppliers",

                    value:
                        toNumber(
                            summary
                                ?.totalSuppliers
                        ),

                    help:
                        "Suppliers purchased from",
                },

                {
                    label:
                        "Purchase Bills",

                    value:
                        toNumber(
                            summary
                                ?.totalBills
                        ),

                    help:
                        "Total purchase bills",
                },

                {
                    label:
                        "Total Purchase",

                    value:
                        formatCurrency(
                            summary
                                ?.grandTotal
                        ),

                    help:
                        "Gross purchase value",
                },

                {
                    label:
                        "Outstanding",

                    value:
                        formatCurrency(
                            summary
                                ?.outstandingAmount
                        ),

                    help:
                        "Amount payable",
                },
            ],
            [
                summary,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const total =
        toNumber(
            pagination?.total
        );

    const currentPage =
        toNumber(
            pagination?.page,
            page
        ) || 1;

    const currentLimit =
        toNumber(
            pagination?.limit,
            limit
        ) || 20;

    const totalPages =
        Math.max(
            toNumber(
                pagination?.totalPages,
                1
            ),
            1
        );

    const fromRecord =
        total > 0
            ? (
                currentPage -
                1
            ) *
                currentLimit +
                1
            : 0;

    const toRecord =
        total > 0
            ? Math.min(
                currentPage *
                    currentLimit,
                total
            )
            : 0;


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="supplier-summary-report">

            {/* =========================================================
                ERROR
            ========================================================= */}

            {errors.supplierSummary && (
                <div className="purchase-report-alert">

                    <FiAlertCircle />

                    <span>
                        {
                            errors
                                .supplierSummary
                        }
                    </span>

                </div>
            )}


            {/* =========================================================
                SUMMARY CARDS
            ========================================================= */}

            <section className="purchase-report-summary-grid">

                {cards.map(
                    (
                        card
                    ) => (
                        <div
                            key={
                                card.label
                            }
                            className="purchase-report-summary-card"
                        >
                            <span>
                                {card.label}
                            </span>

                            <strong>
                                {card.value}
                            </strong>

                            <small>
                                {card.help}
                            </small>
                        </div>
                    )
                )}

            </section>


            {/* =========================================================
                FINANCIAL SUMMARY
            ========================================================= */}

            <section className="purchase-report-tax-summary">

                <div>
                    <span>
                        Purchase
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.grandTotal
                        )}
                    </strong>
                </div>


                <div>
                    <span>
                        Tax
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.taxAmount
                        )}
                    </strong>
                </div>


                <div>
                    <span>
                        Debit Notes
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.debitNoteAmount
                        )}
                    </strong>
                </div>


                <div>
                    <span>
                        Paid
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.paidAmount
                        )}
                    </strong>
                </div>


                <div>
                    <span>
                        Outstanding
                    </span>

                    <strong>
                        {formatCurrency(
                            summary
                                ?.outstandingAmount
                        )}
                    </strong>
                </div>

            </section>


            {/* =========================================================
                TOOLBAR
            ========================================================= */}

            <section className="purchase-report-toolbar">

                <div className="purchase-report-search">

                    <FiSearch />

                    <input
                        type="text"
                        value={
                            searchInput
                        }
                        placeholder="Search supplier, GSTIN, mobile..."
                        onChange={(
                            event
                        ) =>
                            setSearchInput(
                                event
                                    .target
                                    .value
                            )
                        }
                    />

                </div>


                <select
                    value={
                        sortBy
                    }
                    onChange={(
                        event
                    ) => {
                        setSortBy(
                            event
                                .target
                                .value
                        );

                        setPage(1);
                    }}
                >
                    <option value="grandTotal">
                        Purchase Value
                    </option>

                    <option value="outstandingAmount">
                        Outstanding
                    </option>

                    <option value="paidAmount">
                        Paid
                    </option>

                    <option value="taxAmount">
                        Tax
                    </option>

                    <option value="totalBills">
                        Bill Count
                    </option>

                    <option value="supplierName">
                        Supplier Name
                    </option>
                </select>


                <select
                    value={
                        sortOrder
                    }
                    onChange={(
                        event
                    ) => {
                        setSortOrder(
                            event
                                .target
                                .value
                        );

                        setPage(1);
                    }}
                >
                    <option value="desc">
                        Descending
                    </option>

                    <option value="asc">
                        Ascending
                    </option>
                </select>


                <button
                    type="button"
                    className="purchase-report-button"
                    disabled={
                        loading
                            .supplierSummary
                    }
                    onClick={
                        loadData
                    }
                >
                    <FiRefreshCw
                        className={
                            loading
                                .supplierSummary
                                ? "purchase-report-spinner"
                                : ""
                        }
                    />

                    Refresh
                </button>

            </section>


            {/* =========================================================
                TABLE
            ========================================================= */}

            <section className="purchase-report-table-card">

                {loading.supplierSummary &&
                records.length === 0 ? (

                    <div className="purchase-report-empty">

                        <FiRefreshCw className="purchase-report-spinner" />

                        <strong>
                            Loading supplier summary...
                        </strong>

                    </div>

                ) : records.length ===
                    0 ? (

                    <div className="purchase-report-empty">

                        <FiTruck />

                        <strong>
                            No supplier purchases found
                        </strong>

                        <span>
                            No supplier purchase data matches the selected filters.
                        </span>

                    </div>

                ) : (

                    <div className="purchase-report-table-wrapper">

                        <table className="purchase-report-table supplier-summary-table">

                            <thead>

                                <tr>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        GSTIN
                                    </th>

                                    <th className="text-right">
                                        Bills
                                    </th>

                                    <th className="text-right">
                                        Taxable
                                    </th>

                                    <th className="text-right">
                                        Tax
                                    </th>

                                    <th className="text-right">
                                        Purchase
                                    </th>

                                    <th className="text-right">
                                        Debit Notes
                                    </th>

                                    <th className="text-right">
                                        Paid
                                    </th>

                                    <th className="text-right">
                                        Outstanding
                                    </th>

                                    <th className="text-right">
                                        Paid Bills
                                    </th>

                                    <th className="text-right">
                                        Partial
                                    </th>

                                    <th className="text-right">
                                        Unpaid
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {records.map(
                                    (
                                        record,
                                        index
                                    ) => (

                                        <tr
                                            key={
                                                record
                                                    ?.supplierId ||
                                                record
                                                    ?.supplier
                                                    ?._id ||
                                                index
                                            }
                                        >

                                            {/* SUPPLIER */}

                                            <td>

                                                <div className="purchase-report-supplier">

                                                    <strong>
                                                        {getSupplierName(
                                                            record
                                                        )}
                                                    </strong>

                                                    <span>
                                                        {getSupplierCode(
                                                            record
                                                        )}
                                                        {" • "}
                                                        {getSupplierContact(
                                                            record
                                                        )}
                                                    </span>

                                                </div>

                                            </td>


                                            {/* GSTIN */}

                                            <td>
                                                {getSupplierGstin(
                                                    record
                                                )}
                                            </td>


                                            {/* BILLS */}

                                            <td className="text-right">

                                                <strong>
                                                    {toNumber(
                                                        record
                                                            ?.totalBills
                                                    )}
                                                </strong>

                                            </td>


                                            {/* TAXABLE */}

                                            <td className="text-right">
                                                {formatCurrency(
                                                    record
                                                        ?.taxableAmount
                                                )}
                                            </td>


                                            {/* TAX */}

                                            <td className="text-right">
                                                {formatCurrency(
                                                    record
                                                        ?.taxAmount
                                                )}
                                            </td>


                                            {/* PURCHASE */}

                                            <td className="text-right">

                                                <strong>
                                                    {formatCurrency(
                                                        record
                                                            ?.grandTotal
                                                    )}
                                                </strong>

                                            </td>


                                            {/* DEBIT NOTE */}

                                            <td className="text-right">
                                                {formatCurrency(
                                                    record
                                                        ?.debitNoteAmount
                                                )}
                                            </td>


                                            {/* PAID */}

                                            <td className="text-right">

                                                <span className="supplier-summary-paid">
                                                    {formatCurrency(
                                                        record
                                                            ?.paidAmount
                                                    )}
                                                </span>

                                            </td>


                                            {/* OUTSTANDING */}

                                            <td className="text-right">

                                                <strong
                                                    className={
                                                        toNumber(
                                                            record
                                                                ?.outstandingAmount
                                                        ) >
                                                        0
                                                            ? "purchase-report-outstanding"
                                                            : "supplier-summary-zero"
                                                    }
                                                >
                                                    {formatCurrency(
                                                        record
                                                            ?.outstandingAmount
                                                    )}
                                                </strong>

                                            </td>


                                            {/* PAID BILLS */}

                                            <td className="text-right">

                                                <span className="supplier-summary-count supplier-summary-count-paid">
                                                    {toNumber(
                                                        record
                                                            ?.paidBills
                                                    )}
                                                </span>

                                            </td>


                                            {/* PARTIAL */}

                                            <td className="text-right">

                                                <span className="supplier-summary-count supplier-summary-count-partial">
                                                    {toNumber(
                                                        record
                                                            ?.partiallyPaidBills
                                                    )}
                                                </span>

                                            </td>


                                            {/* UNPAID */}

                                            <td className="text-right">

                                                <span className="supplier-summary-count supplier-summary-count-unpaid">
                                                    {toNumber(
                                                        record
                                                            ?.unpaidBills
                                                    )}
                                                </span>

                                            </td>

                                        </tr>
                                    )
                                )}

                            </tbody>

                        </table>

                    </div>

                )}


                {/* =====================================================
                    PAGINATION
                ===================================================== */}

                <div className="purchase-report-pagination">

                    <div>

                        Showing{" "}

                        <strong>
                            {fromRecord}
                        </strong>

                        {" - "}

                        <strong>
                            {toRecord}
                        </strong>

                        {" of "}

                        <strong>
                            {total}
                        </strong>

                        {" suppliers"}

                    </div>


                    <div className="purchase-report-pagination-controls">

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setLimit(
                                    Number(
                                        event
                                            .target
                                            .value
                                    )
                                );

                                setPage(1);
                            }}
                        >

                            <option value={10}>
                                10 / page
                            </option>

                            <option value={20}>
                                20 / page
                            </option>

                            <option value={50}>
                                50 / page
                            </option>

                            <option value={100}>
                                100 / page
                            </option>

                        </select>


                        <button
                            type="button"
                            disabled={
                                currentPage <=
                                1
                            }
                            onClick={() =>
                                setPage(
                                    (
                                        current
                                    ) =>
                                        Math.max(
                                            current -
                                                1,
                                            1
                                        )
                                )
                            }
                        >
                            <FiChevronLeft />
                        </button>


                        <span>

                            Page{" "}

                            <strong>
                                {currentPage}
                            </strong>

                            {" of "}

                            <strong>
                                {totalPages}
                            </strong>

                        </span>


                        <button
                            type="button"
                            disabled={
                                currentPage >=
                                totalPages
                            }
                            onClick={() =>
                                setPage(
                                    (
                                        current
                                    ) =>
                                        Math.min(
                                            current +
                                                1,
                                            totalPages
                                        )
                                )
                            }
                        >
                            <FiChevronRight />
                        </button>

                    </div>

                </div>

            </section>

        </div>
    );
};


export default SupplierSummaryReport;